import { useState } from "react";
import { Button } from "../../../ui";
import { reviewDashboardRequestAPI } from "../../../../utils/api/ludDashboard";
import ActionDialog from "./ActionDialog";
import CancelRequestDialog from "./CancelRequestDialog";
import UploadFileDialog from "./UploadFileDialog";
import RequestActions from "./RequestActions";
import MenuEmailActions, { isMenuEmail } from "./MenuEmailActions";

// The actions of ONE request row, chosen by the request's type and stage. What is
// offered comes from the API's `actions` (role / scope / status are decided by the
// server); this only maps the type's own workflow onto compact buttons:
//
//   statementRequest / taxInvoiceRequest   Upload File, Cancel   (never approved)
//   contractRequest   LUD: Upload Contract -> restaurant: Download + Upload Signed
//                     Contract -> LUD: Download Signed Contract, Approve / Reject
//   contactManagerRequest                  Approve, Reject
//
// Every consequential step has its own confirmation. Cashier branch-change requests
// are handled by BranchQuickActions; every other request keeps the generic quick actions.
export const TYPED_ACTIONS = ["statementRequest", "taxInvoiceRequest", "contractRequest", "contactManagerRequest"];

export default function RowActions({ item, t, lang, branches, onChanged, empty = null }) {
  const [dialog, setDialog] = useState(null);
  const action = item.row?.action;
  const a = item.actions || {};
  const r = t.row;
  const id = item.identity.id;
  const token = localStorage.getItem("auth-token");
  const kinds = a.uploadKinds || [];
  const status = item.status?.native;

  // Menu "Send to App" e-mail requests: draft / failed / sent / cancelled, with their own actions
  if (isMenuEmail(item)) return <MenuEmailActions item={item} t={t} lang={lang} branches={branches} onChanged={onChanged} />;

  if (item.identity.source !== "dashboard" || !TYPED_ACTIONS.includes(action)) {
    return <RequestActions request={item} t={t} lang={lang} onDone={onChanged} empty={empty} />;
  }

  const done = async () => {
    setDialog(null);
    await onChanged?.();
  };
  const buttons = [];
  const deciding = () => {
    if (!a.review) return;
    buttons.push({ key: "approve", label: r.approve, variant: "success", appearance: "filled", run: () => setDialog("approve") });
    buttons.push({ key: "reject", label: r.reject, variant: "danger", appearance: "soft", run: () => setDialog("reject") });
  };

  if (action === "statementRequest" || action === "taxInvoiceRequest") {
    const kind = action === "statementRequest" ? "statement" : "taxInvoice";
    if (kinds.includes(kind)) buttons.push({ key: "upload", label: r.uploadFile, variant: "primary", appearance: "filled", run: () => setDialog({ upload: kind }) });
    if (a.cancel) buttons.push({ key: "cancel", label: r.cancel, variant: "danger", appearance: "soft", run: () => setDialog("cancel") });
  } else if (action === "contractRequest") {
    if (["pending", "inProgress"].includes(status) && kinds.includes("contract")) buttons.push({ key: "upload", label: r.uploadContract, variant: "primary", appearance: "filled", run: () => setDialog({ upload: "contract" }) });
    if (status === "awaitingSignature") {
      if (kinds.includes("signedContract")) buttons.push({ key: "signed", label: r.uploadSigned, variant: "primary", appearance: "filled", run: () => setDialog({ upload: "signedContract" }) });
    }
    if (status === "awaitingContractReview") deciding();
  } else {
    deciding(); // contactManagerRequest
  }
  // every typed request can be canceled while the server still allows it (pending, owner or administrator)
  if (a.cancel && action !== "statementRequest" && action !== "taxInvoiceRequest") buttons.push({ key: "cancel", label: r.cancel, variant: "danger", appearance: "soft", run: () => setDialog("cancel") });
  if (!buttons.length) return empty;

  const upload = dialog?.upload;
  return (
    <>
      {buttons.map((button) => (
        <Button key={button.key} size="sm" variant={button.variant} appearance={button.appearance} disabled={button.disabled} onClick={button.run}>
          {button.label}
        </Button>
      ))}
      {dialog === "approve" && (
        <ActionDialog t={t} title={r.approveTitle} description={r.approveText} confirmLabel={r.confirmApproval} onClose={() => setDialog(null)} onConfirm={async () => { await reviewDashboardRequestAPI(token, id, "approved", ""); await done(); }} />
      )}
      {dialog === "reject" && (
        <ActionDialog t={t} title={r.rejectTitle} description={r.rejectText} reason={{ label: r.rejectReason, required: true }} confirmLabel={r.confirmRejection} danger onClose={() => setDialog(null)} onConfirm={async (reason) => { await reviewDashboardRequestAPI(token, id, "rejected", reason); await done(); }} />
      )}
      {dialog === "cancel" && <CancelRequestDialog requestId={id} t={t} onClose={() => setDialog(null)} onDone={done} />}
      {upload && (
        <UploadFileDialog
          requestId={id}
          kind={upload}
          title={upload === "signedContract" ? r.uploadSigned : upload === "contract" ? r.uploadContract : r.uploadFile}
          confirmLabel={upload === "signedContract" ? r.confirmSignedUpload : r.confirmUpload}
          t={t}
          onClose={() => setDialog(null)}
          onDone={done}
        />
      )}
    </>
  );
}
